'use client';

import clientConfig from '@config/firebase/client';
import {
  NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  NEXT_PUBLIC_FIREBASE_DB_URL,
  NEXT_PUBLIC_FIREBASE_USE_EMULATORS,
  NEXT_PUBLIC_FUNCTIONS_URL,
  STORAGE_EMULATOR_HOST,
} from '@env';
import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import {
  type Auth,
  type User as FirebaseUser,
  type UserInfo as FirebaseUserInfo,
  connectAuthEmulator,
  getAuth,
} from 'firebase/auth';
import {
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type Timestamp,
  collection,
  connectFirestoreEmulator,
  initializeFirestore,
} from 'firebase/firestore';
import { type Functions, connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { type RemoteConfig, getRemoteConfig } from 'firebase/remote-config';
import { type FirebaseStorage, connectStorageEmulator, getStorage } from 'firebase/storage';

import { formatDate } from './date-time';

let app: FirebaseApp;
let auth: Auth;
let firestore: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
let remoteConfig: RemoteConfig;

if (typeof window !== 'undefined' && !getApps().length) {
  app = initializeApp(clientConfig);
  auth = getAuth(app);
  remoteConfig = getRemoteConfig(app);

  firestore = initializeFirestore(app, {
    // ignore explicitly undefined properties
    ignoreUndefinedProperties: true,
    // This fix is to fix the firebase webchannel issue, check https://github.com/firebase/firebase-js-sdk/issues/1674
    experimentalAutoDetectLongPolling: true,
  });

  storage = getStorage(app);
  functions = getFunctions(app);

  // Setup Firebase Emulators
  if (NEXT_PUBLIC_FIREBASE_USE_EMULATORS === 'true') {
    connectAuthEmulator(auth, NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN);

    const dbUrl = new URL(NEXT_PUBLIC_FIREBASE_DB_URL);
    connectFirestoreEmulator(firestore, dbUrl.hostname, Number(dbUrl.port));

    const storageUrl = new URL(STORAGE_EMULATOR_HOST);
    connectStorageEmulator(storage, storageUrl.hostname, Number(storageUrl.port));

    const functionsUrl = new URL(NEXT_PUBLIC_FUNCTIONS_URL);
    connectFunctionsEmulator(functions, functionsUrl.hostname, Number(functionsUrl.port));
  }
}

const formatFirebaseDate = (date?: Timestamp): string =>
  date ? date.toDate().toLocaleString().split(',')[0] : '';

const formatFirebaseDateToStringDate = (date?: Timestamp): string =>
  date ? formatDate(date.toDate()) : '';

type Collection = CollectionReference<DocumentData, DocumentData>;

const collections = {
  cards: (): Collection => collection(firestore, 'cards'),
  cardSaves: (): Collection => collection(firestore, 'card_saves'),
  cardEdits: (): Collection => collection(firestore, 'card_edits'),
  cardPresets: (): Collection => collection(firestore, 'card_presets'),
  bulkUploadRequests: (): Collection => collection(firestore, 'bulk_upload_requests'),

  users: (): Collection => collection(firestore, 'users'),
  userTags: (): Collection => collection(firestore, 'user_tags'),
  userSubscriptions: (userId: string): Collection =>
    collection(firestore, 'users', userId, 'subscriptions'),
  userCheckoutSessions: (userId: string): Collection =>
    collection(firestore, 'users', userId, 'checkout_sessions'),

  contacts: (): Collection => collection(firestore, 'contacts'),
  contactNotes: (): Collection => collection(firestore, 'contact_notes'),

  bbOrgs: (): Collection => collection(firestore, 'bb_orgs'),
  bbContacts: (): Collection => collection(firestore, 'bb_contacts'),
  orgSubscription: (orgId: string): Collection =>
    collection(firestore, 'bb_orgs', orgId, 'subscriptions'),
  orgSamlConfigs: (orgId: string): Collection =>
    collection(firestore, 'bb_orgs', orgId, 'saml_configs'),
  accessories: (): Collection => collection(firestore, 'accessories'),
  accessoryRequests: (): Collection => collection(firestore, 'accessory_requests'),

  stripeProducts: (): Collection => collection(firestore, 'stripe_products'),
  stripeProductPrices: (productId: string): Collection =>
    collection(firestore, 'stripe_products', productId, 'prices'),

  emailSignatures: (): Collection => collection(firestore, 'email_signatures'),
  teamEmailSignatureRequests: (): Collection =>
    collection(firestore, 'team_email_signature_requests'),
  integrations: (): Collection => collection(firestore, 'integrations'),
  personalAccessTokens: (): Collection => collection(firestore, 'personal_access_tokens'),
  virtualBackgrounds: (): Collection => collection(firestore, 'virtual_backgrounds'),
  virtualBackgroundImages: (): Collection => collection(firestore, 'virtual_background_images'),
};

export {
  type FirebaseUser,
  type FirebaseUserInfo,
  auth,
  collections,
  firestore,
  formatFirebaseDate,
  formatFirebaseDateToStringDate,
  functions,
  remoteConfig,
  storage,
};
